import { useEffect, useMemo, useState } from 'react';

import {
  IFeatureOffersQuery,
  IImageFragment,
  IOffersPageMarketingTileFragment,
  useFeatureOffersQuery,
} from 'generated/sanity-graphql';
import { useFeaturesContext } from 'state/features';
import {
  maybeLinkAction,
  maybeLocaleImage,
  maybeLocaleString,
  maybeNumber,
  maybeOffersPageMarketingTiles,
} from 'utils/graphql';

export interface IOffersSelectRestaurantCta {
  body: string | null;
  title: string | null;
  link: string | null;
  ctaButtonText: string | null;
  minimumNumberOfOffers: number | null;
}

export interface IFeatureOffersBrowsingPanel {
  endOfOffersMessage: string | null;
  endOfOffersImage: IImageFragment | null;
  emptyStateTitle: string | null;
  emptyStateBody: string | null;
  emptyStateImage: IImageFragment | null;
  offersSelectRestaurantCta: IOffersSelectRestaurantCta;
}

export interface ILinkAction {
  actionText: string | null;
  route: string | null;
}

export interface IFeatureOffersDetailsPanel {
  emptyStateAction: ILinkAction;
  emptyStateTitle: string | null;
  emptyStateBody: string | null;
  emptyStateImage: IImageFragment | null;
  missingOfferAction: ILinkAction;
  missingOfferTitle: string | null;
  missingOfferBody: string | null;
  missingOfferImage: IImageFragment | null;
}

export interface IFeatureOffers {
  offersBrowsingPanel: IFeatureOffersBrowsingPanel | null;
  offersDetailsPanel: IFeatureOffersDetailsPanel | null;
  defaultBackgroundImage: IImageFragment | null;
  offersPageMarketingTiles: IOffersPageMarketingTileFragment[];
}

const processFeatureOffers = (data: IFeatureOffersQuery | undefined): IFeatureOffers | null => {
  const FeatureOffers = data?.FeatureOffers;

  if (!FeatureOffers) {
    return null;
  }

  const {
    defaultBackgroundImage,
    offersBrowsingPanel,
    offersDetailsPanel,
    offersPageMarketingTiles,
  } = FeatureOffers;

  const { offersSelectRestaurantCta: selectRestaurantCtaData } = offersBrowsingPanel || {};

  return {
    offersBrowsingPanel: offersBrowsingPanel
      ? {
          offersSelectRestaurantCta: {
            title: maybeLocaleString(selectRestaurantCtaData?.title),
            body: maybeLocaleString(selectRestaurantCtaData?.body),
            link: maybeLocaleString(selectRestaurantCtaData?.link),
            minimumNumberOfOffers: maybeNumber(selectRestaurantCtaData?.minimumNumberOfOffers),
            ctaButtonText: maybeLocaleString(selectRestaurantCtaData?.ctaButtonText),
          },
          endOfOffersMessage: maybeLocaleString(offersBrowsingPanel?.endOfOffersMessage),
          endOfOffersImage: maybeLocaleImage(offersBrowsingPanel?.endOfOffersImage),
          emptyStateTitle: maybeLocaleString(offersBrowsingPanel?.emptyStateTitle),
          emptyStateBody: maybeLocaleString(offersBrowsingPanel?.emptyStateBody),
          emptyStateImage: maybeLocaleImage(offersBrowsingPanel?.emptyStateImage),
        }
      : null,
    offersDetailsPanel: offersDetailsPanel
      ? {
          emptyStateAction: maybeLinkAction(offersDetailsPanel?.emptyStateAction),
          emptyStateTitle: maybeLocaleString(offersDetailsPanel?.emptyStateTitle),
          emptyStateBody: maybeLocaleString(offersDetailsPanel?.emptyStateBody),
          emptyStateImage: maybeLocaleImage(offersDetailsPanel?.emptyStateImage),
          missingOfferAction: maybeLinkAction(offersDetailsPanel?.missingOfferAction),
          missingOfferTitle: maybeLocaleString(offersDetailsPanel?.missingOfferTitle),
          missingOfferBody: maybeLocaleString(offersDetailsPanel?.missingOfferBody),
          missingOfferImage: maybeLocaleImage(offersDetailsPanel?.missingOfferImage),
        }
      : null,
    defaultBackgroundImage: defaultBackgroundImage ?? null,
    offersPageMarketingTiles: maybeOffersPageMarketingTiles(offersPageMarketingTiles) ?? null,
  };
};

type IUseFeatureOffersValue = {
  featureOffersLoading: boolean;
  featureOffers: IFeatureOffers | null;
};

export const useFeatureOffers = (): IUseFeatureOffersValue => {
  const { featureOffersId, featureIdsLoading } = useFeaturesContext();
  // wait to fetch featureOffers until featureOffersId exists
  const [featureOffersIdVar, setFeatureOffersIdVar] = useState<string>('');

  useEffect(() => {
    // only setState after useFeaturesContext is complete
    if (!featureIdsLoading && featureOffersId) {
      if (featureOffersIdVar !== featureOffersId) {
        setFeatureOffersIdVar(featureOffersId);
      }
    }
  }, [featureIdsLoading, featureOffersId, featureOffersIdVar]);

  const { data, loading } = useFeatureOffersQuery({
    variables: { featureOffersId: featureOffersIdVar },
    skip: !featureOffersId || !featureOffersIdVar || featureIdsLoading,
  });

  const featureOffers = useMemo(() => processFeatureOffers(data), [data]);
  const featureOffersLoading = featureIdsLoading || loading;
  return {
    featureOffersLoading,
    featureOffers,
  };
};
